// /src/Components/Support/TicketDetailDrawer.jsx
import React, { Fragment, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Toaster, toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";
import { StatusBadge, PriorityBadge } from "./TicketBadges";
import {
  Hash, MessageSquareText, Building2, Flag, User2, CalendarClock, Clock3, CheckCircle2
} from "lucide-react";

export default function TicketDetailDrawer({ open, onClose, ticket }) {
  const { api, adminId } = useContext(GlobalContext);
  const [status, setStatus] = useState(ticket?.status || "O"); // O/P/C
  const [saving, setSaving] = useState(false);

  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const listRef = useRef(null);

  const ticketId = ticket?.pk_ticket_id;

  useEffect(() => setStatus(ticket?.status || "O"), [ticket]);

  const loadConversation = async () => {
    if (!ticketId) return;
    setMsgLoading(true);
    try {
      const res = await api.post("/Support/v1.0/getSupportTicketConversation", {
        ticketid: String(ticketId),
      });
      if (res?.data?.responseCode === "00") {
        setMessages(res.data.response || []);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to load conversation");
        setMessages([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading conversation");
      setMessages([]);
    } finally {
      setMsgLoading(false);
      // scroll to bottom after load
      setTimeout(() => listRef.current?.scrollTo({ top: 9e6, behavior: "smooth" }), 50);
    }
  };

  useEffect(() => {
    if (open && ticketId) loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  const handleUpdateStatus = async () => {
    if (!ticketId) return;
    try {
      setSaving(true);
      const res = await api.post("/Support/v1.0/updateSupportTicket", {
        userid: ticket?.us_code,
        status,
        ticketid: String(ticketId),
      });
      if (res?.data?.responseCode === "00") {
        toast.success("Status updated");
        onClose(true); // ask parent to refetch
      } else {
        toast.error(res?.data?.responseMessage || "Failed to update status");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating status");
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!ticketId) return;
    if (!newMsg.trim()) return toast.info("Type a message first");
    try {
      const res = await api.post("/Support/v1.0/insertSupportTicketConversation", {
        ticketid: String(ticketId),
        userid: String(adminId || ""),
        message: newMsg.trim(),
        sender: "Admin",
      });
      if (res?.data?.responseCode === "00") {
        setNewMsg("");
        loadConversation();
      } else {
        toast.error(res?.data?.responseMessage || "Failed to send message");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error sending message");
    }
  };

  const isAdminMsg = useCallback((m) => {
    if (m && typeof m.sender === "string") return m.sender.toLowerCase() === "admin";
    return m?.fk_user_id == null;
  }, []);

  const metaItems = useMemo(() => {
    if (!ticket) return [];
    return [
      { key: "Ticket Code", val: ticket.ticket_code || "-", icon: Hash, tone: "sky"   },
      { key: "Subject",     val: ticket.subject || "-",     icon: MessageSquareText, tone: "violet" },
      { key: "Department",  val: ticket.department || "-",  icon: Building2, tone: "teal" },
      { key: "Priority",    val: <PriorityBadge value={ticket.priority} />, icon: Flag, tone: "amber" },
      { key: "Customer Id", val: ticket.customer_id || "-", icon: User2, tone: "cyan" },
      { key: "Created",     val: ticket.created_on || "-",  icon: CalendarClock, tone: "rose" },
      { key: "Updated",     val: ticket.updated_on || "-",  icon: Clock3, tone: "indigo" },
      { key: "Closed On",   val: ticket.closed_on || "-",   icon: CheckCircle2, tone: "emerald" },
    ];
  }, [ticket]);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => onClose(false)} className="relative z-50">
        <Toaster position="top-right" />

        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        {/* Drawer container */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-200"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                {/* PANEL: make it full height and flex-col so children can scroll */}
                <Dialog.Panel className="pointer-events-auto w-screen max-w-[480px] sm:max-w-xl">
                  <div className="flex h-screen flex-col bg-white shadow-xl">
                    {/* HEADER (fixed) */}
                    <div className="flex items-center justify-between border-b p-4">
                      <div className="min-w-0">
                        <Dialog.Title className="text-base font-semibold truncate">
                          Ticket #{ticket?.ticket_code} <StatusBadge code={status} />
                        </Dialog.Title>
                        <p className="truncate text-xs text-gray-500">{ticket?.subject}</p>
                      </div>
                      <button
                        onClick={() => onClose(false)}
                        className="h-9 rounded-xl border px-3 text-sm hover:bg-gray-50"
                      >
                        Close
                      </button>
                    </div>

                    {/* BODY (scrollable area) -> must use min-h-0 so inner children can scroll */}
                    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
                      {/* Meta grid */}
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {metaItems.map((m, idx) => {
                          const Icon = m.icon;
                          const toneMap = {
                            sky: "text-sky-700 bg-sky-50 border-sky-200",
                            violet: "text-violet-700 bg-violet-50 border-violet-200",
                            teal: "text-teal-700 bg-teal-50 border-teal-200",
                            amber: "text-amber-700 bg-amber-50 border-amber-200",
                            cyan: "text-cyan-700 bg-cyan-50 border-cyan-200",
                            rose: "text-rose-700 bg-rose-50 border-rose-200",
                            indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
                            emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
                          };
                          return (
                            <div
                              key={idx}
                              className={`flex items-center gap-3 rounded-xl border p-3 ${toneMap[m.tone]}`}
                            >
                              <div className="grid h-9 w-9 place-items-center rounded-lg border bg-white">
                                <Icon size={18} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{m.key}</div>
                                <div className="truncate text-sm">{m.val}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Status action */}
                      <div className="flex flex-wrap items-end gap-2 rounded-xl border p-3">
                        <label className="text-sm">
                          <div className="mb-1 text-xs text-gray-500">Change Status</div>
                          <select
                            className="h-10 w-48 rounded-xl border bg-white px-3 text-sm outline-none"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                          >
                            <option value="O">Open</option>
                            <option value="P">In Progress</option>
                            <option value="C">Closed</option>
                          </select>
                        </label>
                        <button
                          className="ml-auto inline-flex h-10 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
                          onClick={handleUpdateStatus}
                          disabled={saving}
                        >
                          {saving ? "Saving…" : "Update"}
                        </button>
                      </div>

                      {/* Conversation (its own flex column that scrolls) */}
                      <div className="flex min-h-[280px] flex-1 flex-col overflow-hidden rounded-xl border">
                        <div className="flex items-center justify-between border-b px-3 py-2">
                          <div className="text-sm font-semibold">Conversation</div>
                          <button
                            className="h-8 rounded-xl border px-3 text-xs hover:bg-gray-50"
                            onClick={loadConversation}
                            disabled={msgLoading}
                          >
                            {msgLoading ? "Loading…" : "Refresh"}
                          </button>
                        </div>

                        {/* Scrollable list -> flex-1 + min-h-0 */}
                        <div ref={listRef} className="flex-1 min-h-0 space-y-3 overflow-y-auto p-3">
                          {msgLoading ? (
                            <div className="py-6 text-center text-gray-500">Loading…</div>
                          ) : Array.isArray(messages) && messages.length ? (
                            messages.map((m, idx) => {
                              const adminSide = isAdminMsg(m);
                              return (
                                <div
                                  key={m?.pk_conversation_id ?? idx}
                                  className={`flex ${adminSide ? "justify-end" : "justify-start"}`}
                                >
                                  <div
                                    className={`max-w-[80%] rounded-2xl border px-3 py-2 text-sm ${
                                      adminSide
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                        : "border-gray-200 bg-gray-50 text-gray-800"
                                    }`}
                                  >
                                    <div className="whitespace-pre-wrap">{m?.message ?? ""}</div>
                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                                      <span className="rounded border bg-white px-1.5">{adminSide ? "Admin" : "Client"}</span>
                                      <span>{m?.created_on ?? ""}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="py-6 text-center text-gray-500">No messages yet.</div>
                          )}
                        </div>

                        {/* Composer (sticky at bottom of drawer) */}
                        <div className="sticky bottom-0 flex items-center gap-2 border-t bg-white p-3 pb-[env(safe-area-inset-bottom)]">
                          <input
                            className="h-10 flex-1 rounded-xl border px-3 text-sm outline-none"
                            placeholder="Type a message…"
                            value={newMsg}
                            onChange={(e) => setNewMsg(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                          />
                          <button
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-sky-600 px-4 text-sm font-medium text-white hover:bg-sky-700"
                            onClick={sendMessage}
                          >
                            Send
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* FOOTER spacer to respect safe area (also prevents cut-off) */}
                    <div className="h-[env(safe-area-inset-bottom)]" />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
